/** Tenant DTO + enums (backend SubscriptionPlan, etc.). */

export interface Tenant {
  Id: string;
  Code: string;
  Name: string;
  Status: number;
  Plan: number;
  SubscriptionStatus: number;
  TrialEndsOn?: string | null;
  PlanExpiresOn?: string | null;
  CreatedOn: string;
  IsOwner: boolean;
  /** Backend default JSON serializer enum-ийг string-ээр буцаадаг ("Owner"/"Admin"/"Member"). int ирвэл ч handle хийнэ. */
  MyRole: number | string;
  UseLotExpiry: boolean;
  UseUnitConversion: boolean;
  /** 0 = FIFO, 1 = Average. Шинэ бараа үүсгэх үед default-аар тавина. */
  DefaultCostingMethod: number;
}

export interface UpdateTenantRequest {
  Name: string;
}

export interface UpdateInventorySettingsRequest {
  UseLotExpiry: boolean;
  UseUnitConversion: boolean;
  DefaultCostingMethod: number;
}

export interface CreateTenantRequest {
  Name: string;
}

export interface Company {
  Id: string;
  Code: string;
  Name: string;
  BusinessType: number;
  UiProfileCode?: string | null;
  RegisterNo?: string | null;
  Email?: string | null;
  Phone?: string | null;
  TinNo?: string | null;
  VatPayer: boolean;
  CityPayer: boolean;
  Status: number;
  CreatedOn: string;
}

export interface CreateCompanyRequest {
  Code: string;
  Name: string;
  BusinessType: number;
  UiProfileCode?: string | null;
  RegisterNo?: string | null;
  Email?: string | null;
  Phone?: string | null;
}

export interface UpdateCompanyRequest {
  BusinessType: number;
  RegisterNo?: string | null;
  Email?: string | null;
  Phone?: string | null;
}

/**
 * Бизнесийн төрөл (`tmCompany.BusinessType`) — backend enum `BusinessType`-тай
 * ижил утга. UI-д category-аар groups хийнэ:
 *
 *   classic  — 10..13 — одоо ажиллаж байгаа POS/retail UI.
 *   new      — 20 (CarWash) — цоо шинэ интерфейс хөгжиж байгаа (placeholder).
 *   pending  — 30..60 — UI хараахан хөгжөөгүй (header switcher-д disabled).
 *   unknown  — DB-д үлдсэн хуучин 0..7 утга (enum шинэчилсний дараа). UI-д
 *              "Тодорхойгүй" гэж placeholder харуулна.
 */
export type BusinessTypeCategory = 'classic' | 'new' | 'pending' | 'unknown';

export interface BusinessTypeMeta {
  value: number;
  label: string;
  icon: string;
  category: BusinessTypeCategory;
  /** UI-аас сонгох боломжтой эсэх. false бол header switcher-д grey болж disable. */
  enabled: boolean;
}

export const BUSINESS_TYPE_META: BusinessTypeMeta[] = [
  { value: 10, label: 'Дэлгүүр/Бөөний төв', icon: 'bi-shop', category: 'classic', enabled: true },
  { value: 71, label: 'Зочид буудал/Караоке', icon: 'bi-mic', category: 'classic', enabled: true },
];

const UNKNOWN_META: BusinessTypeMeta = {
  value: -1,
  label: 'Тодорхойгүй',
  icon: 'bi-question-circle',
  category: 'unknown',
  enabled: false,
};

export function businessTypeMeta(value: number | string | null | undefined): BusinessTypeMeta {
  // Backend ихэвчлэн int буцаадаг ч JsonStringEnumConverter дараа нэмэгдсэн ч
  // нийцэхийн тулд string-ийг (`"10"` эсвэл `"RetailStore"`) ч хүлээж авна.
  if (value == null) return UNKNOWN_META;
  if (typeof value === 'string') {
    const n = Number(value);
    if (!Number.isNaN(n)) {
      const byNum = BUSINESS_TYPE_META.find((b) => b.value === n);
      if (byNum) return byNum;
    }
    const lower = value.toLowerCase();
    const byName = BUSINESS_TYPE_META.find((b) =>
      ENUM_NAME_BY_VALUE[b.value]?.toLowerCase() === lower,
    );
    if (byName) return byName;
    return UNKNOWN_META;
  }
  return BUSINESS_TYPE_META.find((b) => b.value === value) ?? UNKNOWN_META;
}

/**
 * Backend `BusinessType` enum-ийн name (string serialization үед хүлээн авах
 * fallback-д ашиглана). int буцах нь default тул бараг шаардагдахгүй.
 */
const ENUM_NAME_BY_VALUE: Record<number, string> = {
  10: 'RetailStore',
  71: 'Karaoke',
};

/**
 * CompanySettingsPage-ийн dropdown зэрэг "англи нэртэй" жагсаалт. Шинэ кодонд
 * `BUSINESS_TYPE_META`-ийг ашиглана.
 */
export const BUSINESS_TYPES = BUSINESS_TYPE_META.map((b) => ({
  value: b.value,
  label: b.label,
  enabled: b.enabled,
}));

export const TENANT_USER_ROLES = [
  { value: 0, label: 'Эзэмшигч (Owner)' },
  { value: 1, label: 'Админ (Admin)' },
  { value: 2, label: 'Гишүүн (Member)' },
];
